import { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Menu, X } from "lucide-react";

/**
 * Nav links fall into two kinds:
 *   • type: "anchor" — scrolls to a section id on the home page
 *   • type: "route"  — navigates to a dedicated page
 *
 * When on a non-home route and an anchor link is clicked, we navigate home
 * and defer the scroll to after mount.
 */
const links = [
  { id: "maison",         label: "The Maison",         type: "anchor" },
  { id: "falcon-heart",   label: "Falcon Heart",       type: "anchor" },
  { id: "aviation",       label: "Aviation",           type: "anchor" },
  { id: "ultra-fine",     label: "Ultra Fine Jewelry", type: "route", to: "/ultra-fine-jewelry" },
  { id: "craftsmanship",  label: "Craftsmanship",      type: "anchor" },
  { id: "monaco",         label: "Monaco '26",         type: "anchor" },
  { id: "diamonds",       label: "Diamonds",           type: "anchor" },
  { id: "heritage",       label: "Heritage",           type: "anchor" },
  { id: "invitation",     label: "Request Invitation", type: "anchor" },
];

export default function Navigation() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  // Handle deferred scrolls after cross-page navigation
  useEffect(() => {
    if (location.pathname === "/" && location.state?.scrollTo) {
      const id = location.state.scrollTo;
      // Clear state so refreshes don't re-trigger
      window.history.replaceState({}, "");
      setTimeout(() => {
        const el = document.getElementById(id);
        if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
      }, 60);
    }
  }, [location]);

  const handleLink = (link) => {
    setOpen(false);
    if (link.type === "route") {
      if (location.pathname !== link.to) {
        navigate(link.to);
      } else {
        window.scrollTo({ top: 0, behavior: "smooth" });
      }
      return;
    }
    // anchor
    if (location.pathname === "/") {
      const el = document.getElementById(link.id);
      if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
    } else {
      navigate("/", { state: { scrollTo: link.id } });
    }
  };

  const handleBrand = () => {
    setOpen(false);
    if (location.pathname === "/") {
      window.scrollTo({ top: 0, behavior: "smooth" });
    } else {
      navigate("/");
    }
  };

  const handleInvitation = () => {
    setOpen(false);
    if (location.pathname === "/") {
      const el = document.getElementById("invitation");
      if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
    } else {
      navigate("/", { state: { scrollTo: "invitation" } });
    }
  };

  // Desktop shows a condensed subset so the bar never wraps; the rest are available on mobile.
  const desktopLinks = links.filter((l) =>
    ["falcon-heart", "aviation", "ultra-fine", "craftsmanship", "monaco", "diamonds", "heritage"].includes(l.id)
  );

  return (
    <header
      data-testid="main-navigation"
      className={`fixed top-0 inset-x-0 z-50 transition-all duration-700 ${
        scrolled
          ? "bg-black/70 backdrop-blur-xl border-b border-[rgba(212,175,140,0.20)]"
          : "bg-transparent border-b border-transparent"
      }`}
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-10 flex items-center justify-between h-20">
        <button
          data-testid="nav-brand-logo"
          onClick={handleBrand}
          className="flex items-center gap-3 group"
        >
          <span className="font-serif-display text-xl md:text-2xl tracking-[0.18em] text-white group-hover:text-[var(--pd-gold)] transition-colors duration-500">
            PiérrecDavís
          </span>
        </button>

        <nav className="hidden lg:flex items-center gap-6">
          {desktopLinks.map((l) => (
            <button
              key={l.id}
              data-testid={`nav-link-${l.id}`}
              onClick={() => handleLink(l)}
              className="text-[0.65rem] uppercase tracking-[0.28em] text-white/70 hover:text-[var(--pd-gold)] transition-colors duration-500 whitespace-nowrap"
            >
              {l.label}
            </button>
          ))}
          <button
            data-testid="nav-cta-invitation"
            onClick={handleInvitation}
            className="text-[0.65rem] uppercase tracking-[0.28em] text-[var(--pd-gold)] border border-[var(--pd-gold)] px-5 py-2.5 hover:bg-[var(--pd-gold)] hover:text-black transition-all duration-500 whitespace-nowrap"
          >
            Request Invitation
          </button>
        </nav>

        <button
          data-testid="nav-mobile-toggle"
          onClick={() => setOpen((v) => !v)}
          className="lg:hidden text-white/80 hover:text-[var(--pd-gold)]"
          aria-label="Toggle menu"
        >
          {open ? <X size={22} /> : <Menu size={22} />}
        </button>
      </div>

      {open && (
        <div
          data-testid="nav-mobile-panel"
          className="lg:hidden bg-black/95 backdrop-blur-2xl border-t border-[rgba(212,175,140,0.20)]"
        >
          <div className="px-6 py-6 flex flex-col gap-5">
            {links.map((l) => (
              <button
                key={l.id}
                data-testid={`nav-mobile-link-${l.id}`}
                onClick={() => (l.id === "invitation" ? handleInvitation() : handleLink(l))}
                className="text-left text-sm uppercase tracking-[0.3em] text-white/80 hover:text-[var(--pd-gold)] transition-colors"
              >
                {l.label}
              </button>
            ))}
          </div>
        </div>
      )}
    </header>
  );
}
